// The trip composer, rebuilt to "Landing - Field Notes.dc.html".
//
// One free-text line under a 2px ink rule, a round accent send button, and
// two popovers: When (quick picks + a full month calendar) and Into (grouped
// mood checkboxes). They are mutually exclusive, per the README's Composer
// note. Everything is optional.
//
// The structured duration / party / budget pill rows the old composer had are
// not in this design and are gone from the UI. compileComposerPrompt still
// accepts them, so nothing downstream changed.

import { useEffect, useMemo, useRef, useState } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  ArrowUp, BadgeCheck, CalendarDays, ChevronDown, ChevronLeft, ChevronRight,
  ChevronUp, MapPin, Sparkles,
} from "lucide-react";
import {
  MOOD_GROUPS,
  compileComposerPrompt,
  presetPicksApply,
  type TripPreset,
} from "@/lib/presets";
import { usePlaceSuggestions } from "@/hooks/use-place-suggestions";

export interface PresetApplication {
  preset: TripPreset;
  key: number;
}

interface TripComposerProps {
  application?: PresetApplication | null;
  onStart: (
    prompt: string,
    meta: { vibeCount: number; presetId: string | null; structured: boolean }
  ) => void;
}

const MONTHS = ["January","February","March","April","May","June","July",
  "August","September","October","November","December"];

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const addDays = (d: Date, n: number) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};
const fmt = (d: Date) => `${MONTHS[d.getMonth()].slice(0, 3)} ${d.getDate()}`;
const iso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const sameDay = (a: Date | null, b: Date | null) =>
  !!a && !!b && a.getTime() === b.getTime();

// Pills: ink when the control carries a value, hairline outline when empty.
const PILL_BASE =
  "inline-flex items-center gap-[7px] rounded-full px-4 min-h-[44px] md:min-h-[35px] font-sans text-[11px] uppercase tracking-[0.1em] transition-colors";
const pill = (on: boolean) =>
  `${PILL_BASE} ${on
    ? "bg-ink text-paper border border-ink font-bold"
    : "bg-transparent text-ink-muted border border-rule-dash-2 font-semibold hover:border-ink hover:text-ink"}`;

const DONE_LINK =
  "font-sans text-[10px] font-bold uppercase tracking-[0.14em] text-accent-text h-11 -my-2 inline-flex items-center";

const TripComposer = ({ application, onStart }: TripComposerProps) => {
  const today = useMemo(() => startOfDay(new Date()), []);
  const isMobile = useIsMobile();
  // The mobile artboard carries its own, shorter placeholder.
  const placeholder = isMobile
    ? "Bangkok, a week in September..."
    : "Bangkok, a week in September, big on food...";

  const [query, setQuery] = useState("");
  const [start, setStart] = useState<Date | null>(null);
  const [end, setEnd] = useState<Date | null>(null);
  // A quick pick that implies a duration rather than concrete dates.
  const [quick, setQuick] = useState<string | null>(null);
  // Months forward from the current one; the window is 12 months.
  const [month, setMonth] = useState(0);
  const [moods, setMoods] = useState<Record<string, boolean>>({});
  const [badge, setBadge] = useState<string | null>(null);
  const [whenOpen, setWhenOpen] = useState(false);
  const [intoOpen, setIntoOpen] = useState(false);

  // Destination suggestions. The line is free text ("Bangkok, a week in
  // September, big on food"), not a place field, so suggestions only run
  // while the whole line is still the place: as soon as a comma appears
  // the sentence has moved on to dates and moods, and a dropdown over it
  // would be noise. Editing the comma back out brings them back.
  const [focused, setFocused] = useState(false);
  const [suggestDismissed, setSuggestDismissed] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const placeFragment = query.includes(",") ? "" : query;
  const suggestions = usePlaceSuggestions(
    placeFragment,
    focused && !suggestDismissed
  );
  const suggestOpen = suggestions.length > 0 && focused && !suggestDismissed;

  // Never leave the highlight pointing past the end of a changed list.
  useEffect(() => {
    setActiveSuggestion(-1);
  }, [suggestions]);

  const preset = application?.preset ?? null;

  const acceptSuggestion = (index: number) => {
    const picked = suggestions[index];
    if (!picked) return;
    // Picking the suggestion for the preset's own city is a confirmation,
    // not a change of destination. Google's label carries the country
    // ("Bangkok, Thailand"), which presetPicksApply reads as somewhere
    // else, so writing it back would quietly drop the starter picks the
    // card had just promised. Leave the line as the card set it.
    const keepsPreset =
      !!preset &&
      picked.main.trim().toLowerCase() === preset.destination.toLowerCase();
    setQuery(keepsPreset ? preset.destination : picked.label);
    if (!keepsPreset) setBadge(null);
    setSuggestDismissed(true);
    setActiveSuggestion(-1);
    inputRef.current?.focus();
  };

  // A destination card prefills the line with the city, its moods, and the
  // "A week, flexible" quick pick. No focus() on purpose: the page is
  // smooth-scrolling here, and focusing would cut the scroll short and pop
  // the mobile keyboard over a composer the user has not arrived at yet.
  useEffect(() => {
    if (!application) return;
    const p = application.preset;
    setQuery(p.destination);
    setMoods(Object.fromEntries(p.vibes.map((m) => [m, true])));
    setStart(null);
    setEnd(null);
    setQuick("A week, flexible");
    setBadge(
      p.picksCount
        ? `Starts with ${p.picksCount} verified, creator-sourced ${p.destination} picks`
        : null
    );
  }, [application]);

  const picksApply = presetPicksApply(preset, query);
  const hasRange = !!start && !!end;
  const nights = hasRange ? Math.round((end!.getTime() - start!.getTime()) / 86400000) : 0;
  const activeMoods = Object.keys(moods).filter((m) => moods[m]);

  const whenLabel = hasRange
    ? `${fmt(start!)} – ${start!.getMonth() === end!.getMonth() ? end!.getDate() : fmt(end!)} · ${nights} days`
    : start
      ? `From ${fmt(start)}`
      : quick ?? "When";
  const intoLabel = activeMoods.length
    ? `${activeMoods.length} ${activeMoods.length === 1 ? "mood" : "moods"}`
    : "Into";

  const handleStart = () => {
    if (!query.trim()) return;
    const structured = !!(start || quick || activeMoods.length);
    const prompt = compileComposerPrompt({
      destination: query,
      startDate: start ? iso(start) : null,
      endDate: end ? iso(end) : null,
      // The two open-ended quick picks are duration hints, not dates.
      duration: !start && quick === "A week, flexible" ? "a week" : null,
      party: null,
      budget: null,
      vibes: activeMoods,
    });
    onStart(prompt, {
      vibeCount: activeMoods.length,
      presetId: picksApply && preset ? preset.id : null,
      structured,
    });
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    // The suggestion list owns the arrows and Escape while it is up, and
    // owns Enter only once something in it is highlighted. An Enter with
    // no highlight still starts the trip, so a user who ignores the list
    // never has to dismiss it first.
    // While an IME composition is open the candidate window owns the
    // arrows, Escape and Enter, so the list keeps its hands off them.
    if (suggestOpen && !e.nativeEvent.isComposing) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveSuggestion((i) => (i + 1) % suggestions.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveSuggestion((i) =>
          i <= 0 ? suggestions.length - 1 : i - 1
        );
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setSuggestDismissed(true);
        setActiveSuggestion(-1);
        return;
      }
      if (e.key === "Enter" && activeSuggestion >= 0) {
        e.preventDefault();
        acceptSuggestion(activeSuggestion);
        return;
      }
    }
    // isComposing: an Enter that commits an IME conversion (CJK input) must
    // never submit the composer.
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      handleStart();
    }
  };

  const pickDay = (date: Date) => {
    setQuick(null);
    if (!start || (start && end)) {
      setStart(date);
      setEnd(null);
      return;
    }
    if (date < start) {
      setEnd(start);
      setStart(date);
      return;
    }
    if (sameDay(date, start)) {
      setStart(null);
      setEnd(null);
      return;
    }
    setEnd(date);
  };

  const quickPicks = useMemo(() => {
    const dow = today.getDay();
    const sat = addDays(today, dow === 6 ? 7 : dow === 0 ? 6 : 6 - dow);
    const nextMon = addDays(today, (8 - dow) % 7 || 7);
    return [
      { label: "This weekend", apply: () => { setStart(sat); setEnd(addDays(sat, 1)); setQuick("This weekend"); setMonth(sat.getMonth() - today.getMonth()); } },
      { label: "Next week", apply: () => { setStart(nextMon); setEnd(addDays(nextMon, 6)); setQuick("Next week"); setMonth(nextMon.getMonth() - today.getMonth()); } },
      { label: "A week, flexible", apply: () => { setStart(null); setEnd(null); setQuick("A week, flexible"); } },
      { label: "Not sure yet", apply: () => { setStart(null); setEnd(null); setQuick("Not sure yet"); } },
    ];
  }, [today]);

  const firstOfMonth = new Date(today.getFullYear(), today.getMonth() + month, 1);
  const daysInMonth = new Date(firstOfMonth.getFullYear(), firstOfMonth.getMonth() + 1, 0).getDate();
  const lead = (firstOfMonth.getDay() + 6) % 7;

  return (
    <div>
      {/* The line. 2px ink rule under the input, per the README's rules note. */}
      <div className="relative">
        <div className="flex items-end gap-3 md:gap-3.5 border-b-2 border-ink pb-3 md:pb-[13px]">
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setBadge(null); // editing the text retires the picks promise
              setSuggestDismissed(false); // typing reopens the list
            }}
            onKeyDown={onKeyDown}
            onFocus={() => setFocused(true)}
            // The list closes on blur, but an option's mousedown is
            // prevented below, so clicking one never counts as leaving.
            onBlur={() => setFocused(false)}
            placeholder={placeholder}
            aria-label="Describe your trip"
            role="combobox"
            aria-expanded={suggestOpen}
            aria-controls="destination-suggestions"
            aria-autocomplete="list"
            aria-activedescendant={
              suggestOpen && activeSuggestion >= 0
                ? `destination-suggestion-${activeSuggestion}`
                : undefined
            }
            autoComplete="off"
            className="flex-1 min-w-0 min-h-[44px] bg-transparent border-none outline-none font-serif text-[17px] md:text-[21px] text-ink placeholder:text-ink-faint p-0"
          />
          <button
            type="button"
            onClick={handleStart}
            aria-label="Start planning"
            style={{ opacity: query.trim() ? 1 : 0.55 }}
            className="h-[46px] w-[46px] md:h-[50px] md:w-[50px] shrink-0 rounded-full bg-accent text-on-accent flex items-center justify-center transition-all duration-200 hover:bg-accent-hover md:hover:translate-x-[3px]"
          >
            <ArrowUp className="h-[18px] w-[18px] md:h-[19px] md:w-[19px]" />
          </button>
        </div>

        {/* Suggestions hang off the rule rather than pushing the pills
            down, so the composer never jumps under the typing hand. */}
        {suggestOpen && (
          <ul
            id="destination-suggestions"
            role="listbox"
            aria-label="Destination suggestions"
            // Leaving the list drops the highlight, so a mouse that only
            // crossed the rows on its way elsewhere does not leave Enter
            // pointing at a suggestion instead of starting the trip.
            onMouseLeave={() => setActiveSuggestion(-1)}
            className="absolute left-0 right-0 top-full z-20 mt-[-1px] max-h-[292px] overflow-y-auto border border-rule bg-card shadow-[0_18px_44px_-18px_rgba(var(--fn-shadow-rgb),0.3)]"
          >
            {suggestions.map((s, i) => (
              <li key={s.placeId || s.label} role="none">
                <button
                  type="button"
                  id={`destination-suggestion-${i}`}
                  role="option"
                  aria-selected={i === activeSuggestion}
                  // Keeps focus in the input, so the blur above never
                  // closes the list out from under the click.
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => acceptSuggestion(i)}
                  onMouseEnter={() => setActiveSuggestion(i)}
                  className={`flex w-full items-baseline gap-2.5 px-4 min-h-[44px] py-2 text-left border-b border-rule-hair last:border-b-0 ${
                    i === activeSuggestion ? "bg-paper-warm" : ""
                  }`}
                >
                  <MapPin className="h-[13px] w-[13px] shrink-0 self-center text-ink-faint" />
                  <span className="font-serif text-[15px] md:text-[16px] text-ink truncate">
                    {s.main}
                  </span>
                  {s.secondary && (
                    <span className="font-serif italic text-[13px] text-ink-faint truncate">
                      {s.secondary}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex items-center gap-2.5 md:gap-3 mt-4 md:mt-[18px] flex-wrap">
        <button
          type="button"
          onClick={() => { setWhenOpen((o) => !o); setIntoOpen(false); }}
          className={pill(hasRange || !!start || !!quick)}
        >
          <CalendarDays className="h-[13px] w-[13px]" />
          {whenLabel}
          {whenOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        </button>
        <button
          type="button"
          onClick={() => { setIntoOpen((o) => !o); setWhenOpen(false); }}
          className={pill(activeMoods.length > 0)}
        >
          <Sparkles className="h-[13px] w-[13px]" />
          {intoLabel}
          {intoOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        </button>
        <span className="hand text-[18px] text-ink-faint ml-1">
          both optional, the concierge fills gaps
        </span>
      </div>

      {badge && picksApply && (
        <div className="inline-flex items-center gap-[7px] mt-3.5 px-3 py-[7px] bg-accent-tint-2 font-sans text-[10.5px] font-bold uppercase tracking-[0.14em] text-accent-text">
          <BadgeCheck className="h-[13px] w-[13px]" />
          {badge}
        </div>
      )}

      {whenOpen && (
        <div className="mt-3.5 border border-rule bg-white p-[18px_20px] max-w-[480px] shadow-[0_18px_44px_-18px_rgba(var(--fn-shadow-rgb),0.3)]">
          <div className="flex gap-2 mb-4 flex-wrap">
            {quickPicks.map((q) => (
              <button
                key={q.label}
                type="button"
                onClick={q.apply}
                className={`${pill(quick === q.label)} !px-4`}
              >
                {q.label}
              </button>
            ))}
          </div>

          <div className="flex items-center justify-between mb-2.5">
            <button
              type="button"
              onClick={() => setMonth((m) => Math.max(0, m - 1))}
              disabled={month <= 0}
              aria-label="Previous month"
              className="h-11 w-11 -my-1.5 inline-flex items-center justify-center disabled:cursor-default"
            >
              <ChevronLeft className={`h-3.5 w-3.5 ${month <= 0 ? "text-rule-dash-2" : "text-ink"}`} />
            </button>
            <span className="font-serif text-[15px]">
              {MONTHS[firstOfMonth.getMonth()]} {firstOfMonth.getFullYear()}
            </span>
            <button
              type="button"
              onClick={() => setMonth((m) => Math.min(11, m + 1))}
              disabled={month >= 11}
              aria-label="Next month"
              className="h-11 w-11 -my-1.5 inline-flex items-center justify-center disabled:cursor-default"
            >
              <ChevronRight className={`h-3.5 w-3.5 ${month >= 11 ? "text-rule-dash-2" : "text-ink"}`} />
            </button>
          </div>

          <div className="grid grid-cols-7 text-center font-sans text-[9px] font-bold tracking-[0.08em] text-ink-faint mb-1.5">
            {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
              <span key={i}>{d}</span>
            ))}
          </div>

          <div className="grid grid-cols-7 text-center gap-y-0.5">
            {Array.from({ length: lead }).map((_, i) => (
              <span key={`lead-${i}`} className="invisible h-11" />
            ))}
            {Array.from({ length: daysInMonth }, (_, i) => {
              const date = new Date(firstOfMonth.getFullYear(), firstOfMonth.getMonth(), i + 1);
              const past = date < today;
              const isEdge = sameDay(date, start) || sameDay(date, end);
              const inRange = !!start && !!end && date > start && date < end;
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => pickDay(date)}
                  disabled={past}
                  aria-disabled={past}
                  className={[
                    "h-11 w-full font-sans text-xs",
                    past ? "text-rule-dash-2 line-through cursor-default" : "text-ink cursor-pointer",
                    isEdge ? "bg-accent-text text-on-accent font-semibold" : inRange ? "bg-accent-tint" : "",
                  ].join(" ")}
                >
                  {i + 1}
                </button>
              );
            })}
          </div>

          <div className="flex justify-between items-center border-t border-rule-hair mt-3.5 pt-3 gap-3">
            <span className="font-serif text-[15px] text-accent-text min-w-0">
              {hasRange ? `${fmt(start!)} – ${fmt(end!)} · ${nights} days` : start ? `From ${fmt(start)}` : ""}
            </span>
            <span className="flex gap-[18px] shrink-0">
              {(start || end) && (
                <button
                  type="button"
                  onClick={() => { setStart(null); setEnd(null); setQuick(null); }}
                  className="font-sans text-[10px] font-bold uppercase tracking-[0.14em] text-ink-muted hover:text-danger h-11 -my-2 inline-flex items-center"
                >
                  Clear dates
                </button>
              )}
              <button type="button" onClick={() => setWhenOpen(false)} className={DONE_LINK}>
                Done
              </button>
            </span>
          </div>
        </div>
      )}

      {intoOpen && (
        <div className="mt-3.5 border border-rule bg-white p-[20px_22px] max-w-[560px] shadow-[0_18px_44px_-18px_rgba(var(--fn-shadow-rgb),0.3)]">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-[26px] gap-y-[18px]">
            {MOOD_GROUPS.map((g) => (
              <div key={g.label}>
                <div className="hand text-[18px] text-accent-text mb-1.5">{g.label}</div>
                <div className="flex flex-col">
                  {g.moods.map((label) => {
                    const on = !!moods[label];
                    return (
                      <button
                        key={label}
                        type="button"
                        onClick={() => setMoods((m) => ({ ...m, [label]: !m[label] }))}
                        className={`flex items-center gap-[9px] text-left text-[13px] min-h-[44px] ${on ? "text-ink font-semibold" : "text-ink-muted"}`}
                      >
                        <span
                          className={`relative h-[15px] w-[15px] shrink-0 border-[1.5px] ${on ? "border-accent" : "border-rule-dash"}`}
                        >
                          {on && (
                            <svg viewBox="0 0 16 16" fill="none" className="absolute -top-[3px] -left-[2px] h-[19px] w-[19px]">
                              <path d="M2 9 C5 12, 6 13, 7 11 C9 6, 11 3, 15 1" stroke="var(--fn-accent)" strokeWidth="2.4" strokeLinecap="round" />
                            </svg>
                          )}
                        </span>
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-between items-center border-t border-rule-hair mt-4 pt-3">
            <span className="font-serif italic text-[13.5px] text-ink-faint">
              Pick a few. It reads between the lines.
            </span>
            <button type="button" onClick={() => setIntoOpen(false)} className={DONE_LINK}>
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TripComposer;
